import type { ReactElement, SVGProps } from 'react'

/**
 * CatGO's own icon set: round caps, round joins, a friendly 2.2px stroke on a
 * 24px grid. Deliberately not an icon library — the whole set costs less than
 * a single library import, and the capture mark is ours.
 */
export type IconName =
  | 'home' | 'map' | 'compass' | 'user' | 'lens' | 'cat-camera'
  | 'back' | 'forward' | 'close' | 'check' | 'plus' | 'minus'
  | 'flip' | 'star' | 'star-filled' | 'heart' | 'heart-filled' | 'lock' | 'pin' | 'calendar'
  | 'photo' | 'video' | 'settings' | 'search' | 'bell' | 'trash'
  | 'edit' | 'warning' | 'cloud' | 'users' | 'eye' | 'eye-off'
  | 'chevron-down' | 'chevron-right' | 'sun' | 'moon' | 'device'
  | 'share' | 'logout' | 'sparkle' | 'link' | 'block' | 'info'
  | 'grid' | 'list' | 'target'
  | 'refresh' | 'clock' | 'cat' | 'paw'
  | 'mail-paw' | 'frame-cat' | 'cat-door' | 'yarn' | 'dots' | 'paw-line'

const paths: Record<IconName, ReactElement> = {
  home: <><path d="M3.5 10.5 12 4l8.5 6.5" /><path d="M5.5 9.5V19a1.5 1.5 0 0 0 1.5 1.5h10A1.5 1.5 0 0 0 18.5 19V9.5" /></>,
  map: <><path d="m3 6.5 6-2.5 6 2.5 6-2.5v13l-6 2.5-6-2.5-6 2.5z" /><path d="M9 4v13M15 6.5v13" /></>,
  compass: <><circle cx="12" cy="12" r="8.5" /><path d="m15 9-1.8 4.2L9 15l1.8-4.2z" /></>,
  user: <><circle cx="12" cy="8.5" r="3.8" /><path d="M5 20a7 7 0 0 1 14 0" /></>,
  // The capture mark: a camera with cat ears, solid, the lens cut out.
  'cat-camera': (
    <>
      <path
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="1.2"
        fillRule="evenodd"
        d="M4 9 5.3 4.4 9.2 8h5.6l3.9-3.6L20 9v8a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 17zM12 10.1a3.5 3.5 0 1 0 0 7 3.5 3.5 0 1 0 0-7z"
      />
      <circle cx="12" cy="13.6" r="1.3" fill="currentColor" stroke="none" />
    </>
  ),
  // An aperture: used for "loading" and for the camera when it is an outline.
  lens: <><circle cx="12" cy="12" r="8.4" /><circle cx="12" cy="12" r="3.4" /><path d="M12 3.6v4.2M20.4 12h-4.2M12 20.4v-4.2M3.6 12h4.2" /></>,
  cat: <><path d="M4.8 11 5.6 4.2 9.3 6.6a4.5 4.5 0 0 1 5.4 0l3.7-2.4.8 6.8A7.3 7.3 0 1 1 4.8 11z" /><path d="M9.4 12.4h.01M14.6 12.4h.01" strokeWidth="2.8" /></>,
  paw: (
    <g fill="currentColor" stroke="none">
      <ellipse cx="12" cy="15.2" rx="3.6" ry="3" />
      <circle cx="7.2" cy="11.2" r="1.7" />
      <circle cx="10.2" cy="7.6" r="1.7" />
      <circle cx="13.8" cy="7.6" r="1.7" />
      <circle cx="16.8" cy="11.2" r="1.7" />
    </g>
  ),
  back: <path d="M14.5 5.5 8 12l6.5 6.5" />,
  forward: <path d="M9.5 5.5 16 12l-6.5 6.5" />,
  close: <path d="m6.5 6.5 11 11M17.5 6.5l-11 11" />,
  check: <path d="m5 12.6 4.6 4.4L19 7" />,
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  flip: <><path d="M3.6 9.4a8.4 8.4 0 0 1 13.9-3.2l2.9 2.6" /><path d="M20.4 14.6a8.4 8.4 0 0 1-13.9 3.2l-2.9-2.6" /><path d="M20.4 4.2v4.6h-4.6M3.6 19.8v-4.6h4.6" /></>,
  star: <path d="m12 3.8 2.6 5.4 5.9.8-4.3 4.1 1.1 5.9-5.3-2.9-5.3 2.9 1.1-5.9L3.5 10l5.9-.8z" />,
  'star-filled': <path d="m12 3.8 2.6 5.4 5.9.8-4.3 4.1 1.1 5.9-5.3-2.9-5.3 2.9 1.1-5.9L3.5 10l5.9-.8z" fill="currentColor" />,
  heart: <path d="M12 20.5s-7.5-4.6-7.5-10A4 4 0 0 1 12 8a4 4 0 0 1 7.5 2.5c0 5.4-7.5 10-7.5 10z" />,
  'heart-filled': <path d="M12 20.5s-7.5-4.6-7.5-10A4 4 0 0 1 12 8a4 4 0 0 1 7.5 2.5c0 5.4-7.5 10-7.5 10z" fill="currentColor" />,
  lock: <><rect x="4.6" y="10.4" width="14.8" height="9.6" rx="3" /><path d="M8.2 10.4V7.8a3.8 3.8 0 0 1 7.6 0v2.6" /></>,
  pin: <><path d="M12 21s6.5-6 6.5-10.5A6.5 6.5 0 1 0 5.5 10.5C5.5 15 12 21 12 21Z" /><circle cx="12" cy="10.5" r="2.3" /></>,
  target: <><circle cx="12" cy="12" r="8.4" /><circle cx="12" cy="12" r="3" /><path d="M12 1.8v3M12 19.2v3M22.2 12h-3M4.8 12h-3" /></>,
  refresh: <><path d="M20.5 12a8.5 8.5 0 1 1-2.5-6l2.5 2.4" /><path d="M20.7 3.6v4.9h-4.9" /></>,
  calendar: <><rect x="3.8" y="5.4" width="16.4" height="14.8" rx="3" /><path d="M3.8 10h16.4M8.4 3.2v4M15.6 3.2v4" /></>,
  clock: <><circle cx="12" cy="12" r="8.6" /><path d="M12 7.2V12l3.2 2" /></>,
  photo: <><rect x="3.4" y="5" width="17.2" height="14" rx="3" /><circle cx="8.6" cy="10" r="1.6" /><path d="m4.6 17.4 4.6-4.4 3.4 3 2.8-2.4 4 3.8" /></>,
  video: <><rect x="2.8" y="6.4" width="13" height="11.2" rx="3" /><path d="m16.8 10.6 4.4-2.6v8l-4.4-2.6z" /></>,
  settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2v.2a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1.1h-.2a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3h.1a1.7 1.7 0 0 0 1-1.5v-.2a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8v.1a1.7 1.7 0 0 0 1.5 1h.2a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></>,
  search: <><circle cx="10.8" cy="10.8" r="6.6" /><path d="m15.6 15.6 4.4 4.4" /></>,
  bell: <><path d="M17.6 10.4a5.6 5.6 0 1 0-11.2 0c0 5-2 6.4-2 6.4h15.2s-2-1.4-2-6.4Z" /><path d="M13.6 20a1.9 1.9 0 0 1-3.2 0" /></>,
  trash: <><path d="M4.4 6.8h15.2M9.2 6.8V4.6h5.6v2.2" /><path d="M17.6 6.8 17 19.2a1.4 1.4 0 0 1-1.4 1.2H8.4A1.4 1.4 0 0 1 7 19.2L6.4 6.8" /></>,
  edit: <><path d="M15.8 4.6a2.1 2.1 0 0 1 3 3L8.4 18l-4 1 1-4z" /></>,
  warning: <><path d="M12 4.4 21 19.6H3z" /><path d="M12 10v4M12 17h.01" /></>,
  cloud: <><path d="M6.8 18.4a4 4 0 0 1-.4-8 5.8 5.8 0 0 1 11.2 1.2 3.4 3.4 0 0 1-.6 6.8z" /></>,
  users: <><circle cx="9.4" cy="8.4" r="3.4" /><path d="M3.4 19.4a6 6 0 0 1 12 0" /><path d="M16.4 5.4a3.4 3.4 0 0 1 0 6.6M17.8 14.6a6 6 0 0 1 2.8 4.8" /></>,
  eye: <><path d="M2.4 12S5.8 5.8 12 5.8 21.6 12 21.6 12 18.2 18.2 12 18.2 2.4 12 2.4 12Z" /><circle cx="12" cy="12" r="3" /></>,
  'eye-off': <><path d="M9.6 5.9A7.8 7.8 0 0 1 12 5.6c6.2 0 9.6 6.2 9.6 6.2a15.9 15.9 0 0 1-3.1 3.9M6.2 7.4A15.6 15.6 0 0 0 2.4 11.8s3.4 6.2 9.6 6.2a8.6 8.6 0 0 0 3.5-.7" /><path d="m3.4 3.4 17.2 17.2M9.9 9.9a3 3 0 0 0 4.2 4.2" /></>,
  'chevron-down': <path d="m6.4 9.4 5.6 5.6 5.6-5.6" />,
  'chevron-right': <path d="m9.4 6.4 5.6 5.6-5.6 5.6" />,
  sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2.6v2.2M12 19.2v2.2M2.6 12h2.2M19.2 12h2.2M5.4 5.4l1.6 1.6M17 17l1.6 1.6M18.6 5.4 17 7M7 17l-1.6 1.6" /></>,
  moon: <path d="M20.4 13.6A8.4 8.4 0 1 1 10.4 3.6a6.6 6.6 0 0 0 10 10Z" />,
  device: <><rect x="6.4" y="2.8" width="11.2" height="18.4" rx="3" /><path d="M10.8 18.4h2.4" /></>,
  share: <><path d="M12 15.4V3.8M8.4 7.4 12 3.8l3.6 3.6" /><path d="M5.4 13.4v5.4a1.4 1.4 0 0 0 1.4 1.4h10.4a1.4 1.4 0 0 0 1.4-1.4v-5.4" /></>,
  logout: <><path d="M9.6 20.2H5.8a1.6 1.6 0 0 1-1.6-1.6V5.4a1.6 1.6 0 0 1 1.6-1.6h3.8" /><path d="M15.6 16.4 20 12l-4.4-4.4M20 12H9.2" /></>,
  // The AI mark. A four-pointed spark, never a magic wand or a robot.
  sparkle: <path d="M12 3.4 13.7 9a3.4 3.4 0 0 0 2.3 2.3l5.6 1.7-5.6 1.7a3.4 3.4 0 0 0-2.3 2.3L12 22.6 10.3 17a3.4 3.4 0 0 0-2.3-2.3L2.4 13l5.6-1.7A3.4 3.4 0 0 0 10.3 9z" />,
  link: <><path d="M10 13.8a3.6 3.6 0 0 0 5.4.4l2.6-2.6a3.6 3.6 0 0 0-5.1-5.1l-1.5 1.5" /><path d="M14 10.2a3.6 3.6 0 0 0-5.4-.4L6 12.4a3.6 3.6 0 0 0 5.1 5.1l1.5-1.5" /></>,
  block: <><circle cx="12" cy="12" r="8.6" /><path d="m5.9 5.9 12.2 12.2" /></>,
  info: <><circle cx="12" cy="12" r="8.6" /><path d="M12 11v5.4M12 7.8h.01" /></>,
  grid: <><rect x="3.8" y="3.8" width="6.4" height="6.4" rx="2" /><rect x="13.8" y="3.8" width="6.4" height="6.4" rx="2" /><rect x="3.8" y="13.8" width="6.4" height="6.4" rx="2" /><rect x="13.8" y="13.8" width="6.4" height="6.4" rx="2" /></>,
  list: <path d="M4.4 6.6h15.2M4.4 12h15.2M4.4 17.4h15.2" />,
  // A letter someone sealed with a paw: a follow request, waiting. (§0052)
  'mail-paw': (
    <>
      <rect x="3.4" y="5.4" width="17.2" height="13.2" rx="2" />
      <path d="m4.4 7.1 7.6 5.7 7.6-5.7" />
      <g fill="currentColor" stroke="none">
        <ellipse cx="16.7" cy="15.9" rx="1.5" ry="1.1" />
        <circle cx="15" cy="14" r=".6" />
        <circle cx="16.7" cy="13.4" r=".6" />
        <circle cx="18.4" cy="14" r=".6" />
      </g>
    </>
  ),
  // A framed portrait, ears peeking over the mat: the collection.
  'frame-cat': <><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M9.4 10.7 8.9 7.9l2.2 1.3M14.6 10.7l.5-2.8-2.2 1.3" /><circle cx="12" cy="13.7" r="4" /></>,
  // The door with its cat flap: privacy is deciding who comes in.
  'cat-door': <><rect x="5.5" y="3.5" width="13" height="17" rx="1.6" /><circle cx="15.6" cy="12" r=".9" fill="currentColor" stroke="none" /><path d="M9.4 20.5v-2.7a2.6 2.6 0 0 1 5.2 0v2.7" /></>,
  // A ball of yarn with its loose end: where things get adjusted.
  yarn: <><circle cx="11" cy="12" r="7" /><path d="M5.2 9.3c3.7-2.3 7.9-2.3 11.6 0M4.4 13.5c4.2-2.4 9-2.4 13.2 0M7.5 17.6c2.4-1.4 4.9-1.6 7.3-.6M17.8 12.8c2.3.4 3.9 1.9 3.4 4.4" /></>,
  dots: (
    <g fill="currentColor" stroke="none">
      <circle cx="5.2" cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="18.8" cy="12" r="1.6" />
    </g>
  ),
  // The outline paw: the stamped footprints that walk across the profile.
  'paw-line': <><ellipse cx="12" cy="15.6" rx="5" ry="3.7" /><ellipse cx="6.3" cy="9.7" rx="1.8" ry="2.4" transform="rotate(-18 6.3 9.7)" /><ellipse cx="12" cy="7.7" rx="1.8" ry="2.4" /><ellipse cx="17.7" cy="9.7" rx="1.8" ry="2.4" transform="rotate(18 17.7 9.7)" /></>,
}

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName
  size?: number
  strokeWidth?: number
}

export function Icon({ name, size = 22, strokeWidth = 2.2, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {paths[name]}
    </svg>
  )
}
